/*
| Pure row model behind the planning week grid: which rows exist and in what
| order, which row a shift belongs to, how overlapping shifts stack into lanes
| and what each row sums to. Extracted from calendar.ts so the grid's behaviour
| is testable without a DOM — calendar.ts keeps only the rendering and wiring.
|
| Two independent axes: rows (people | workplace) and, within people, grouping
| (role | workplace | none). Dragging changes whatever the rows represent, so
| the row identity produced here is also the drop target identity.
*/

export type RowsMode = 'people' | 'workplace';
export type GroupMode = 'role' | 'workplace' | 'none';

export type Employee = {
    id: number;
    name: string;
    nickname?: string | null;
    planning_role?: string | null;
    workplace_id?: number | null;
    initials?: string;
    color?: string;
};

export type Workplace = { id: number; name: string; abbreviation?: string | null; color?: string | null };

export type Role = { key: string; name: string };

export type Shift = {
    id: number;
    user_id: number | null;
    workplace_id?: number | null;
    parent_shift_id?: number | null;
    starts_at: string;
    ends_at: string;
    net_minutes?: number | null;
    shift_type?: { key?: string | null } | null;
};

export type GridRow = {
    key: string;
    label: string;
    sublabel?: string | null;
    /** People rows: the employee the row belongs to; null on the open-shifts row. */
    empId?: number | null;
    /** Workplace rows: the workplace the row belongs to; null on the "no workplace" row. */
    workplaceId?: number | null;
    initials?: string;
    color?: string | null;
};

export type GridGroup = { key: string; label: string; rows: GridRow[] };

/** Open-shifts lane is rendered above the groups, so it is not one of them. */
export type GridModel = { openRow: GridRow | null; groups: GridGroup[] };

/** Business band the grid positions shifts on. */
export const AXIS_START = 6 * 60;
export const AXIS_SPAN = 16 * 60;

/**
 * Stacked-lane height. Workplace rows routinely hold four or more people at
 * once, so they trade block height for the number of people that stay visible.
 */
export const LANE_HEIGHT: Record<RowsMode, number> = { people: 34, workplace: 26 };

export const wallDate = (v: string): string => (v.match(/(\d{4}-\d{2}-\d{2})/) ?? ['', ''])[1];
export const wallTime = (v: string): string => (v.match(/[T ](\d{2}:\d{2})/) ?? ['', ''])[1];
export const toMinutes = (hhmm: string): number => {
    const [h, m] = hhmm.split(':').map(Number);

    return h * 60 + m;
};

export const isBreak = (shift: Shift): boolean => shift.shift_type?.key === 'break';
export const isOvernight = (shift: Shift): boolean => wallDate(shift.starts_at) !== wallDate(shift.ends_at);

/** Which row a shift belongs to — also the drop-target identity for that mode. */
export const rowKeyOfShift = (shift: Shift, mode: RowsMode): string => {
    if (mode === 'workplace') {
        return shift.workplace_id ? `w${shift.workplace_id}` : 'wnone';
    }

    return shift.user_id === null ? 'open' : String(shift.user_id);
};

export const rowKeyOfWorkplace = (id: number | null): string => (id === null ? 'wnone' : `w${id}`);

const employeeRow = (employee: Employee): GridRow => ({
    key: String(employee.id),
    label: employee.name,
    sublabel: employee.nickname ?? null,
    empId: employee.id,
    initials: employee.initials,
    color: employee.color,
});

/**
 * People rows grouped by the chosen dimension. Employees with no value for that
 * dimension collect in a trailing group rather than disappearing.
 */
const peopleGroups = (
    group: GroupMode,
    employees: Employee[],
    workplaces: Workplace[],
    roles: Role[],
    labels: { noWorkplace: string; noRole: string },
): GridGroup[] => {
    if (group === 'none') {
        return employees.length ? [{ key: 'all', label: '', rows: employees.map(employeeRow) }] : [];
    }

    const byRole = group === 'role';
    const order: string[] = byRole ? roles.map((r) => r.key) : workplaces.map((w) => String(w.id));
    const names = new Map<string, string>(
        byRole
            ? roles.map((r): [string, string] => [r.key, r.name])
            : workplaces.map((w): [string, string] => [String(w.id), w.name]),
    );
    const keyOf = (e: Employee): string => {
        const raw = byRole ? (e.planning_role ?? '') : String(e.workplace_id ?? '');

        return names.has(raw) ? raw : '';
    };

    const buckets = new Map<string, Employee[]>();
    for (const employee of employees) {
        const key = keyOf(employee);
        const bucket = buckets.get(key);
        if (bucket) {
            bucket.push(employee);
            continue;
        }
        buckets.set(key, [employee]);
    }

    return [...order, '']
        .filter((key) => buckets.has(key))
        .map((key) => ({
            key: key === '' ? 'none' : key,
            label: names.get(key) ?? (byRole ? labels.noRole : labels.noWorkplace),
            rows: (buckets.get(key) ?? []).map(employeeRow),
        }));
};

/**
 * Workplace rows: every active workplace in its configured order, so a room
 * nobody covers stays visible as an empty row, plus a trailing catch-all only
 * when some shift really has no workplace.
 */
const workplaceGroups = (workplaces: Workplace[], shifts: Shift[], noWorkplace: string): GridGroup[] => {
    const rows: GridRow[] = workplaces.map((workplace) => ({
        key: rowKeyOfWorkplace(workplace.id),
        label: workplace.name,
        sublabel: workplace.abbreviation ?? null,
        workplaceId: workplace.id,
        color: workplace.color,
    }));

    if (shifts.some((shift) => !isBreak(shift) && !shift.workplace_id)) {
        rows.push({ key: 'wnone', label: noWorkplace, workplaceId: null });
    }

    return rows.length ? [{ key: 'wp', label: '', rows }] : [];
};

/**
 * The grid's rows for the current axes. `employees` and `shifts` are expected to
 * be filtered already — this decides structure, not visibility.
 */
export function buildGridModel(input: {
    mode: RowsMode;
    group: GroupMode;
    employees: Employee[];
    workplaces: Workplace[];
    roles: Role[];
    shifts: Shift[];
    openLane: boolean;
    labels: { open: string; noWorkplace: string; noRole: string };
}): GridModel {
    if (input.mode === 'workplace') {
        return { openRow: null, groups: workplaceGroups(input.workplaces, input.shifts, input.labels.noWorkplace) };
    }

    return {
        openRow: input.openLane ? { key: 'open', label: input.labels.open, empId: null } : null,
        groups: peopleGroups(input.group, input.employees, input.workplaces, input.roles, input.labels),
    };
}

/**
 * Greedy interval partitioning per row and day, so shifts that overlap in time
 * stack instead of covering each other. Non-overlapping shifts keep lane 0 and
 * stay side by side on the time axis. Breaks inherit their parent's lane.
 */
export function assignLanes(
    shifts: Shift[],
    mode: RowsMode,
): { lanes: Map<number, number>; maxLane: Map<string, number> } {
    const lanes = new Map<number, number>();
    const maxLane = new Map<string, number>();

    const byCell = new Map<string, Shift[]>();
    for (const shift of shifts) {
        if (isBreak(shift)) continue;
        const cell = `${rowKeyOfShift(shift, mode)}|${wallDate(shift.starts_at)}`;
        const bucket = byCell.get(cell);
        if (bucket) {
            bucket.push(shift);
            continue;
        }
        byCell.set(cell, [shift]);
    }

    byCell.forEach((list, cell) => {
        list.sort((a, b) => toMinutes(wallTime(a.starts_at)) - toMinutes(wallTime(b.starts_at)));
        const laneEnds: number[] = [];
        let highest = 0;
        for (const shift of list) {
            const start = toMinutes(wallTime(shift.starts_at));
            const end = isOvernight(shift) ? AXIS_START + AXIS_SPAN : toMinutes(wallTime(shift.ends_at));
            let lane = laneEnds.findIndex((taken) => taken <= start);
            if (lane === -1) lane = laneEnds.length;
            laneEnds[lane] = end;
            lanes.set(shift.id, lane);
            if (lane > highest) highest = lane;
        }
        maxLane.set(cell, highest);
    });

    for (const shift of shifts) {
        if (!isBreak(shift)) continue;
        lanes.set(shift.id, (shift.parent_shift_id && lanes.get(shift.parent_shift_id)) || 0);
    }

    return { lanes, maxLane };
}

/**
 * Net minutes per row. People rows report worked time of the assigned person;
 * workplace rows report the room's planned load, so open shifts count there —
 * an unfilled slot is still an hour the workplace needs covered.
 */
export function rowMinutes(shifts: Shift[], mode: RowsMode): Map<string, number> {
    const totals = new Map<string, number>();

    for (const shift of shifts) {
        if (isBreak(shift)) continue;
        if (mode === 'people' && shift.user_id === null) continue;
        const key = rowKeyOfShift(shift, mode);
        totals.set(key, (totals.get(key) ?? 0) + (shift.net_minutes ?? 0));
    }

    return totals;
}
